/**
 * A2 — Aviso en el Dashboard mientras el bufete no tiene ningún cliente: lleva a
 * «Primeros pasos». Se puede descartar (se recuerda en este navegador).
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Rocket, X } from 'lucide-react'
import { api } from '../lib/api'
import { useAuth } from '../contexts/AuthContext'
import type { ClientListResponse } from '@toga/shared'

const DISMISS_KEY = 'toga.onboarding.dismissed'

function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1'
  } catch {
    return false
  }
}

export default function OnboardingBanner() {
  const auth = useAuth()
  const role = auth.status === 'authenticated' ? auth.user.role : null
  const eligible = role === 'ADMIN' || role === 'ABOGADO'
  const [empty, setEmpty] = useState(false)
  const [dismissed, setDismissed] = useState(readDismissed)

  useEffect(() => {
    if (!eligible || dismissed) return
    let alive = true
    api
      .get<ClientListResponse>('/clients?pageSize=1')
      .then((r) => alive && setEmpty(r.total === 0))
      .catch(() => alive && setEmpty(false))
    return () => {
      alive = false
    }
  }, [eligible, dismissed])

  if (!eligible || dismissed || !empty) return null

  const dismiss = () => {
    setDismissed(true)
    try {
      localStorage.setItem(DISMISS_KEY, '1')
    } catch {
      /* sin almacenamiento: se vuelve a mostrar la próxima vez */
    }
  }

  return (
    <div className="rounded-xl border border-toga-500/30 bg-toga-500/10 px-4 py-3 flex flex-wrap items-center gap-3">
      <Rocket size={18} className="text-toga-300 shrink-0" />
      <div className="min-w-0 flex-1 basis-64 text-sm">
        <div className="t-primary font-semibold">Empieza por tus clientes</div>
        <div className="t-muted text-xs">
          Aún no hay ningún cliente. Importa tu cartera desde un Excel o sigue la lista de primeros
          pasos.
        </div>
      </div>
      <Link
        to="/clients/import"
        className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700"
      >
        Importar desde Excel
      </Link>
      <Link
        to="/onboarding"
        className="px-3 py-1.5 rounded-lg text-xs border border-base t-secondary hover:bg-hover"
      >
        Primeros pasos
      </Link>
      <button
        type="button"
        onClick={dismiss}
        title="No volver a mostrar"
        className="w-7 h-7 grid place-items-center rounded-lg t-muted hover:bg-hover"
      >
        <X size={14} />
      </button>
    </div>
  )
}
